import { useState, useEffect, useCallback } from "react";
import { Download, Smartphone, X, Share, Plus, CheckCircle } from "lucide-react";

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
const isInStandaloneMode = () =>
  (typeof window !== "undefined" && window.matchMedia('(display-mode: standalone)').matches) ||
  (typeof navigator !== "undefined" && navigator.standalone);

/**
 * Persistent "Install App" button. Visible on every page (until installed).
 * - Chrome / Android / Edge / desktop: clicking triggers the browser install prompt
 * - iOS Safari: clicking opens an instructions modal
 * - Already installed: shows "✓ Installed" badge instead
 *
 * Props:
 *   variant — "primary" | "ghost" | "icon"   (style)
 *   label   — text override (defaults to "Install App")
 */
const InstallAppButton = ({ variant = "primary", label = "Install App" }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showIosModal, setShowIosModal] = useState(false);
  const [showUnsupported, setShowUnsupported] = useState(false);

  useEffect(() => {
    if (isInStandaloneMode()) {
      setInstalled(true);
      return;
    }
    const onBeforePrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onBeforePrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforePrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleClick = useCallback(async () => {
    if (installed) return;
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") setInstalled(true);
      setDeferredPrompt(null);
      return;
    }
    if (isIOS()) {
      setShowIosModal(true);
      return;
    }
    setShowUnsupported(true);
    setTimeout(() => setShowUnsupported(false), 4500);
  }, [deferredPrompt, installed]);

  // Style variants
  const baseClasses = "inline-flex items-center gap-2 font-semibold uppercase tracking-wider transition-all";
  const styles = {
    primary: `${baseClasses} bg-[#D62828] hover:bg-[#B91C1C] text-white text-xs px-4 py-2 rounded-sm`,
    ghost: `${baseClasses} text-gray-300 hover:text-white text-sm`,
    icon: `${baseClasses} text-gray-300 hover:text-[#D62828] text-xs`,
  };
  const styleCls = styles[variant] || styles.primary;

  if (installed) {
    return (
      <span className={`${baseClasses} text-green-400 text-xs`} data-testid="install-app-installed">
        <CheckCircle className="w-4 h-4" /> App Installed
      </span>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className={styleCls}
        data-testid="install-app-button"
        title={isIOS() ? "Install on iPhone — tap for instructions" : "Install Cut Above as an app on your device"}
      >
        {variant === "icon" ? <Smartphone className="w-4 h-4" /> : <Download className="w-4 h-4" />}
        <span>{label}</span>
      </button>

      {/* iOS Safari instructions modal */}
      {showIosModal && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={() => setShowIosModal(false)}
          data-testid="install-ios-modal"
        >
          <div
            className="bg-[#121212] border border-white/10 rounded-sm max-w-sm w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <p className="font-heading text-xl text-white">Install on iPhone</p>
              <button onClick={() => setShowIosModal(false)} className="text-gray-500 hover:text-white" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-gray-300 text-sm mb-4 leading-relaxed">
              In Safari, follow these 3 steps to add Cut Above to your home screen:
            </p>
            <ol className="space-y-3">
              <li className="flex items-center gap-3 text-sm text-gray-300">
                <span className="w-7 h-7 bg-[#D62828] rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 text-xs">1</span>
                Tap <Share className="w-4 h-4 text-blue-400 inline mx-1" /> Share in the toolbar
              </li>
              <li className="flex items-center gap-3 text-sm text-gray-300">
                <span className="w-7 h-7 bg-[#D62828] rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 text-xs">2</span>
                Choose <Plus className="w-4 h-4 text-white inline mx-1" /> <span className="font-semibold">Add to Home Screen</span>
              </li>
              <li className="flex items-center gap-3 text-sm text-gray-300">
                <span className="w-7 h-7 bg-[#D62828] rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 text-xs">3</span>
                Tap <span className="text-[#D4AF37] font-semibold mx-1">Add</span> — done!
              </li>
            </ol>
            <button
              onClick={() => setShowIosModal(false)}
              className="w-full mt-5 bg-[#D62828] hover:bg-[#B91C1C] text-white py-2.5 rounded-sm font-bold text-xs uppercase tracking-wider"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Unsupported browser hint */}
      {showUnsupported && (
        <div
          className="fixed bottom-6 right-6 z-[90] max-w-xs bg-[#1A1A1A] border border-yellow-500/30 rounded-sm shadow-2xl p-4"
          data-testid="install-unsupported"
        >
          <p className="text-yellow-300 text-xs font-semibold mb-1">Browser doesn't support install</p>
          <p className="text-gray-300 text-xs leading-relaxed">
            Open this site in <span className="text-white font-semibold">Chrome</span>, <span className="text-white font-semibold">Edge</span>, or <span className="text-white font-semibold">Safari (iOS)</span> to install Cut Above as an app.
          </p>
        </div>
      )}
    </>
  );
};

export default InstallAppButton;
